import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import eye from 'four-nets/icons/svg/icon_views.svg';
import mapMarker from 'four-nets/icons/svg/icon_place.svg';
import subSection from 'four-nets/icons/svg/icon_sub-section.svg';
import { Site } from 'four-nets/site';

import Localizable from 'lib/intl/localizable';
import withDialog from 'lib/alerts/with-dialog';
import FormattedDateAgoView from 'lib/intl/formatted-date-ago-view';
import Styled from 'styled';
import LinkView from 'lib/ui/link-view';

import users from 'four-nets/icons/svg/icon_users.svg';
import { UserBazaarLink } from 'four-nets/bazaar/user-bazaar/user-bazaar-link';
import { CategoryLink } from 'four-nets/bazaar/categories/category-link';
import { CountyLink } from 'four-nets/bazaar/categories/county-link';
import { Product } from 'four-nets/bazaar/bazaar';
import Badge from 'four-nets/ui-v2/badges/badge/badge';
import HighlightedBadge from 'four-nets/ui-v2/badges/highlighted-badge/highlighted-badge';
import UnhighlightedBadge from 'four-nets/ui-v2/badges/unhighlighted-badge/unhighlighted-badge';

import defaultStyles from './product-status-bar-view.scss';

export interface Props {
    styles: typeof defaultStyles;
    product: Product;
}

@withDialog
@Localizable()
@observer
class ProductStatusBarView extends React.Component<Props> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    categoryLink: object;
    countyLink?: object;
    link: object;

    constructor(props: Props) {
        super(props);
        const { product } = props;

        this.categoryLink = new CategoryLink({
            category: product.category,
        });

        if (product.county) {
            this.countyLink = new CountyLink({
                county: product.county,
            });
        }

        this.link = new UserBazaarLink({
            user: product.user,
        });
    }

    render() {
        const { product } = this.props;
        const styles = this.props.styles!;

        return (
            <div className={styles.mainWrapper}>
                <div className={styles.rowWrapper}>
                    {product.isVendor ? (
                        <HighlightedBadge uppercase={true}>
                            <FormattedMessage id='NEW_GOODS' />
                        </HighlightedBadge>
                    ) : (
                        <UnhighlightedBadge uppercase={true}>
                            {this.context.site && this.context.site.isGarden ? (
                                <FormattedMessage id='PRODUCTS_FROM_SECOND_HAND' />
                            ) : (
                                <FormattedMessage id='PRODUCTS_FROM_BAZAAR' />
                            )}
                        </UnhighlightedBadge>
                    )}
                    <Badge>
                        <FormattedDateAgoView value={product.startTime} />
                    </Badge>
                    <Badge src={users}>{product.buyersCount}</Badge>
                    <Badge src={eye}>{product.viewsCount}</Badge>
                </div>
                <div className={styles.bottomRowWrapper}>
                    <UnhighlightedBadge src={subSection}>
                        <LinkView link={this.categoryLink} className={styles.categoryLink}>
                            {product.category.name}
                        </LinkView>
                    </UnhighlightedBadge>
                    {this.countyLink ? (
                        <UnhighlightedBadge src={mapMarker}>
                            <LinkView link={this.countyLink} className={styles.categoryLink}>
                                {product.county.name}
                            </LinkView>
                        </UnhighlightedBadge>
                    ) : null}
                    {product.propertyValues
                        ? product.propertyValues.map(propertyValue =>
                              propertyValue.background ? (
                                  <Link
                                      to={propertyValue.url}
                                      key={propertyValue.value}
                                      className={styles.colorPropertyValue}
                                  >
                                      <div className={styles.color} style={{ background: propertyValue.background }} />
                                      <div className={styles.colorLabel}>{propertyValue.value}</div>
                                  </Link>
                              ) : (
                                  <HighlightedBadge key={propertyValue.value}>
                                      <Link
                                          to={propertyValue.url}
                                          title={propertyValue.property}
                                          className={styles.categoryLink}
                                      >
                                          {propertyValue.value}
                                      </Link>
                                  </HighlightedBadge>
                              )
                          )
                        : null}
                </div>
            </div>
        );
    }
}

export default Styled(defaultStyles)(ProductStatusBarView);
